import { isRealCompany, isSunrun } from '@terros/common'
import { useConnectFeature, useCustomLocationManagementFeature, useTerrosMcpFeature } from '@hooks'
import {
  type FeatureState,
  translateLabels,
  useExternalLinks,
  useFeature,
  useProfile,
  useQuizFeature,
  useTranslation,
  useWorkflowForCanvassFeature,
} from '@front/model'
import { NAV_SECTIONS, type PageRoute } from '../navigation'
import { MilestonePill } from '../MilestonePill'
import { CONNECT_VIEW_PERMISSIONS, INTEGRATION_MANAGE_PERMISSIONS, isIntegrationsVisible } from './integrationsAccess'
import { createLinkRoutes } from './getLinkRoutes'

const [MY_WORK, PEOPLE_AND_PLACES, PERFORMANCE, PLATFORM, INTERNAL] = NAV_SECTIONS

export type SidebarRoutes = {
  routes: PageRoute[]
  isPending: boolean
}

export function useRoutes(): SidebarRoutes {
  const { user, company, isLoading: isProfileLoading } = useProfile()
  const { t } = useTranslation()
  const { isEnabled: isIntegrationsEnabled, isLoading: isIntegrationsLoading } = useIsIntegrationsEnabled()
  const { data: links = [], isLoading: isLinksLoading } = useExternalLinks({})

  const { isEnabled: isComplianceEnabled, isLoading: isComplianceLoading } = useFeature({
    featureId: 'Feature:Compliance',
  })

  const { isEnabled: isWorkflowEnabled, isLoading: isWorkflowLoading } = useWorkflowForCanvassFeature()

  const { isEnabled: isConversationsEnabled, isLoading: isConversationsLoading } = useFeature({
    featureId: 'Feature:RecordedConversations',
  })

  const { isEnabled: isQuizEnabled, isLoading: isQuizLoading, milestone: quizMilestone } = useQuizFeature()

  const {
    isEnabled: isAIEnabled,
    isLoading: isAILoading,
    milestone: aiMilestone,
  } = useFeature({
    featureId: 'Feature:TerrosAIChat',
  })

  const { isEnabled: isCustomLocationManagementEnabled, isLoading: isCustomLocationManagementLoading } =
    useCustomLocationManagementFeature()

  const isPending = isSidebarPending([
    isProfileLoading,
    isLinksLoading,
    isIntegrationsLoading,
    isComplianceLoading,
    isWorkflowLoading,
    isConversationsLoading,
    isQuizLoading,
    isAILoading,
    isCustomLocationManagementLoading,
  ])

  const routes: PageRoute[] = translateLabels(
    [
      { path: '/', label: 'Home', matchFormat: 'exact', icon: 'home', section: MY_WORK },
      {
        path: '/calendar',
        label: 'Calendar',
        icon: 'calendar_month',
        section: MY_WORK,
      },
      {
        path: '/prospecting',
        label: 'Prospecting Activity',
        icon: 'location_on',
        hidden: isWorkflowEnabled || isWorkflowLoading,
        section: MY_WORK,
      },
      {
        path: '/activity',
        label: 'Activity',
        icon: 'search_activity',
        scope: 'account:list',
        matchFormat: 'exact',
        hidden: !isWorkflowEnabled || isWorkflowLoading,
        section: MY_WORK,
      },
      {
        path: '/activityTrail',
        label: 'Activity Trail',
        icon: 'route',
        matchFormat: 'exact',
        section: MY_WORK,
      },
      {
        path: '/account',
        label: 'Accounts',
        icon: 'table',
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/areaManagement',
        label: 'Area Management',
        icon: 'assignment',
        matchFormat: 'exact',
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/location/list',
        label: 'Custom Locations',
        icon: 'location_on',
        scope: 'admin',
        matchFormat: 'exact',
        hidden: !isCustomLocationManagementEnabled || isCustomLocationManagementLoading,
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/location/geocode',
        label: 'Batch Geocoder',
        icon: 'edit_location_alt',
        matchFormat: 'exact',
        scope: 'location:manage',
        section: INTERNAL,
      },
      {
        path: '/dnk',
        label: 'Do Not Knock',
        icon: 'do_not_touch',
        scope: 'manager',
        hidden: isWorkflowEnabled || isWorkflowLoading,
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/closers',
        label: 'Closer Availability',
        icon: 'calendar_check',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/team',
        label: 'Teams',
        icon: 'groups',
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/user',
        label: 'Users',
        icon: 'group',
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/sunrun/oktaUsers',
        label: 'Sunrun Okta Users',
        icon: 'solar_power',
        scope: 'admin',
        matchFormat: 'exact',
        hidden: !isSunrun(user?.companyId),
        section: PEOPLE_AND_PLACES,
      },
      {
        path: '/companies',
        label: 'Companies',
        icon: 'business',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/program',
        label: 'Programs',
        icon: 'calendar_clock',
        section: PERFORMANCE,
      },
      {
        path: '/stats',
        label: 'Stats',
        icon: 'bar_chart',
        section: PERFORMANCE,
      },
      {
        path: '/dashboard',
        label: 'Dashboards',
        icon: 'dashboard',
        section: PERFORMANCE,
        scope: 'dashboard:list',
      },
      {
        path: '/competition',
        label: 'Competitions',
        icon: 'emoji_events',
        scope: 'game:list',
        section: PERFORMANCE,
      },
      {
        path: '/compliance',
        label: 'Compliance',
        icon: 'policy',
        scope: 'compliance:manage',
        hidden: !isComplianceEnabled || isComplianceLoading,
        section: PERFORMANCE,
      },
      {
        path: '/conversations',
        label: 'Skill Insights',
        icon: 'mic',
        scope: 'conversation:list',
        hidden: !isConversationsEnabled || isConversationsLoading,
        section: PERFORMANCE,
      },
      {
        path: '/quiz',
        label: 'Quizzes',
        icon: 'queue',
        scope: 'quiz:manage',
        hidden: !isQuizEnabled || isQuizLoading,
        rightSection: <MilestonePill milestone={quizMilestone} />,
        section: PERFORMANCE,
      },
      {
        path: '/feature',
        label: 'Features',
        icon: 'extension',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/betausers',
        label: 'Beta Users',
        icon: 'engineering',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/task/managers',
        label: 'Tasks',
        icon: 'assignment_turned_in',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/task/troubleshoot',
        label: 'Tasks Troubleshooting',
        icon: 'troubleshoot',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/task/management',
        label: 'Task Management',
        icon: 'task',
        scope: 'task:manage',
        section: PLATFORM,
      },
      {
        path: '/questions',
        label: 'Startup Questions',
        icon: 'quiz',
        scope: 'question:manage',
        section: PLATFORM,
      },
      {
        path: '/short/reports',
        label: 'Review Shorts',
        icon: 'video_library',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/recordings',
        label: 'Review Recordings',
        icon: 'mic',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/chat/new',
        label: 'Terros AI',
        icon: 'chat',
        hidden: !isAIEnabled || isAILoading,
        rightSection: <MilestonePill milestone={aiMilestone} />,
        section: PLATFORM,
      },
      {
        path: '/short/upload',
        label: 'Upload Shorts',
        icon: 'video_call',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/cloneTool',
        label: 'Clone Tool',
        icon: 'content_copy',
        scope: 'tantalim',
        section: INTERNAL,
      },
      {
        path: '/sampleComponents',
        label: 'Components',
        icon: 'code',
        scope: 'tantalim',
        section: INTERNAL,
      },
      { path: '/location/audit', label: 'Activity Trail', icon: 'table_view', scope: 'admin', section: PLATFORM },
      ...createLinkRoutes(links),
      {
        path: '/demo-tools',
        label: 'Demo Tools',
        icon: 'science',
        matchFormat: 'exact',
        hidden: isRealCompany(company),
        section: PLATFORM,
      },
      {
        path: '/integration',
        label: 'Integrations',
        icon: 'hub',
        section: PLATFORM,
        hidden: !isIntegrationsEnabled,
      },
      {
        path: '/settings',
        label: 'Settings',
        icon: 'settings',
        section: PLATFORM,
      },
    ],
    t
  )

  return { routes, isPending }
}

const isSidebarPending = (loadingFlags: boolean[]): boolean => loadingFlags.some((isLoading) => isLoading)

const useIsIntegrationsEnabled = (): FeatureState => {
  const { isAdmin, hasPermission } = useProfile()
  const { isEnabled: isConnectEnabled, isLoading: isConnectLoading } = useConnectFeature()
  const { isEnabled: isTerrosMcpEnabled, isLoading: isTerrosMcpLoading } = useTerrosMcpFeature()
  const canViewConnect = CONNECT_VIEW_PERMISSIONS.some(hasPermission)
  const hasConnectAccess = isConnectEnabled && canViewConnect
  const canManageIntegrations = INTEGRATION_MANAGE_PERMISSIONS.some(hasPermission)
  const isEnabled = isIntegrationsVisible({
    isConnectLoading,
    hasConnectAccess,
    isAdmin,
    canManageIntegrations,
    isTerrosMcpEnabled,
    isTerrosMcpLoading,
  })

  return { isEnabled, isLoading: isConnectLoading || isTerrosMcpLoading }
}
